module.exports = {
	important: true,
	theme: {
		colors: {
			primary: { default: '#005F3D', dark: '#002D1D' },
			secondary: { default: '#BAFF79', dark: '#8DE23C' },
			mint: { default: '#E3FFC9', dark: '#8DE23C' },
			red: { default: '#A00000', light: '#F5E5E5', dark: '#900000' },
			green: {
				light: '#008858',
				lightMedium: '#4A9F55',
				medium: '#669A7E',
				mediumDark: '#1A6F50',
				dark: '#01412F',
				highlight: '#96C40E',
			},
			orange: { default: '#FF8C00', light: '#FFD199', dark: '#CC7000' },
			yellow: { default: '#FFCD00' },
			black: { default: '#002D1D', dark: '#000000' },
			white: '#FFFFFF',
			gray: {
				50: '#F2F4F4',
				100: '#E5EAE8',
				200: '#CCD5D2',
				300: '#B2C0BB',
				400: '#99ABA5',
				500: '#80968E',
				600: '#668177',
				700: '#4D6C61',
				800: '#33574A',
				900: '#1A4234',
			},
			inherit: 'inherit',
			transparent: 'transparent',
			warning: {
				ok: '#005F3D',
				maintainance: '#0B6090',
				notice: '#FFAA00',
				partialerror: '#E37D7D',
				error: '#A00000',
			},
			social: {
				xing: '#005A5F',
				facebook: '#3E5294',
				youtube: '#FF0000',
				instagram: '#E1306C',
				tiktok: '#00F2EA',
				linkedin: '#2867B2',
			},
			function: {
				added: '#8DE23C',
				deleted: '#A00000',
			},
		},
		borderRadius: {
			none: '0',
			default: '7px',
			xs: '2px',
			sm: '0.46666667rem',
			lg: '0.6rem',
			xl: '20px',
			'2xl': '25px',
			'3xl': '30px',
			'4xl': '35px',
			'5xl': '40px',
			half: '50%',
			full: '100%',
		},
		borderWidth: {
			default: '1px',
			0: '0',
			2: '2px',
			4: '4px',
			5: '5px',
			8: '8px',
			10: '10px',
			20: '20px',
		},
		spacing: {
			px: '1px',
			0: '0',
			1: '5px',
			2: '10px',
			3: '15px',
			4: '20px',
			5: '25px',
			6: '30px',
			7: '35px',
			8: '40px',
			9: '45px',
			10: '50px',
			11: '60px',
			12: '80px',
			13: '100px',
			14: '150px',
			15: '200px',
			16: '250px',
			17: '300px',
			18: '350px',
			19: '400px',
			20: '450px',
			'100vw': '100vw',
		},
		cursor: {
			auto: 'auto',
			default: 'default',
			pointer: 'pointer',
			wait: 'wait',
			text: 'text',
			move: 'move',
			'not-allowed': 'not-allowed',
			crosshair: 'crosshair',
			'zoom-in': 'zoom-in',
			'zoom-out': 'zoom-out',
			grabbing: 'grabbing',
		},
		maxWidth: (theme, { breakpoints }) => ({
			none: 'none',
			full: '100%',
			...theme('spacing'),
			...breakpoints(theme('screens')),
		}),
		maxHeight: (theme) => ({
			none: 'none',
			full: '100%',
			...theme('spacing'),
		}),
		fontFamily: {
			display: ['rb-galano-neue', 'sans-serif'],
			icon: ['olb-icons'],
		},
		fontSize: {
			xs: 12,
			s: 13,
			sm: 14,
			base: 15,
			medium: 16,
			lg: 18,
			xl: 20,
			'2xl': 26,
			'3xl': 30,
			'4xl': 40,
			'5xl': 50,
			'6xl': 60,
			'icon-xs': 12,
			'icon-sm': 15,
			'icon-md': 18,
			'icon-base': 24,
			'icon-lg': 30,
			'icon-xl': 46,
			'icon-xxl': 58,
			//paragraph font sizes [font-size, line-height]
			'p-base': ['14px', '150%'],
			'p-lg': ['15px', '160%'],
			//button font sizes
			'btn-base': ['16px', '150%'],
			'btn-lg': ['16px', '150%'],
			// heading font sizes
			'heading-sm': ['24px', '120%'],
			'heading-lg': ['32px', '120%'],
		},
		lineHeight: {
			none: '1',
			tight: '1.2',
			medium: '1.3',
			snug: '1.35',
			normal: '1.5',
			relaxed: '1.6',
			loose: '2',
			initial: 'initial',
			inherit: 'inherit',
			'1': '22px',
			'2': '27px',
			'3': '36px',
			'4': '40px',
			'5': '52px',
			'6': '60px',
			'7': '70px',
			'8': '22px',
		},
		screens: {
			sm: '640px',
			md: '768px',
			stagemd: '900px',
			lg: '1024px',
			xl: '1280px',
			xxl: '1380px',
			print: { raw: 'print' },
		},
		flex: {
			'1': '1 1 0%',
			auto: '1 1 auto',
			initial: '0 1 auto',
			inherit: 'inherit',
			none: 'none',
			'2': '2 2 0%',
			'3': '3 3 0%',
		},
		gap: (theme) => theme('spacing'),
		gridTemplateColumns: {
			none: 'none',
			'1': 'repeat(1, minmax(0, 1fr))',
			'2': 'repeat(2, minmax(0, 1fr))',
			'3': 'repeat(3, minmax(0, 1fr))',
			'4': 'repeat(4, minmax(0, 1fr))',
			'5': 'repeat(5, minmax(0, 1fr))',
			'6': 'repeat(6, minmax(0, 1fr))',
			'7': 'repeat(7, minmax(0, 1fr))',
			'8': 'repeat(8, minmax(0, 1fr))',
			'9': 'repeat(9, minmax(0, 1fr))',
			'10': 'repeat(10, minmax(0, 1fr))',
			'11': 'repeat(11, minmax(0, 1fr))',
			'12': 'repeat(12, minmax(0, 1fr))',
		},
		gridColumn: {
			auto: 'auto',
			'span-1': 'span 1 / span 1',
			'span-2': 'span 2 / span 2',
			'span-3': 'span 3 / span 3',
			'span-4': 'span 4 / span 4',
			'span-5': 'span 5 / span 5',
			'span-6': 'span 6 / span 6',
			'span-7': 'span 7 / span 7',
			'span-8': 'span 8 / span 8',
			'span-9': 'span 9 / span 9',
			'span-10': 'span 10 / span 10',
			'span-11': 'span 11 / span 11',
			'span-12': 'span 12 / span 12',
		},
		gridColumnStart: {
			auto: 'auto',
			'1': '1',
			'2': '2',
			'3': '3',
			'4': '4',
			'5': '5',
			'6': '6',
			'7': '7',
			'8': '8',
			'9': '9',
			'10': '10',
			'11': '11',
			'12': '12',
			'13': '13',
		},
		gridColumnEnd: {
			auto: 'auto',
			'1': '1',
			'2': '2',
			'3': '3',
			'4': '4',
			'5': '5',
			'6': '6',
			'7': '7',
			'8': '8',
			'9': '9',
			'10': '10',
			'11': '11',
			'12': '12',
			'13': '13',
		},
		gridTemplateRows: {
			none: 'none',
			'1': 'repeat(1, minmax(0, 1fr))',
			'2': 'repeat(2, minmax(0, 1fr))',
			'3': 'repeat(3, minmax(0, 1fr))',
			'4': 'repeat(4, minmax(0, 1fr))',
			'5': 'repeat(5, minmax(0, 1fr))',
			'6': 'repeat(6, minmax(0, 1fr))',
		},
		gridRow: {
			auto: 'auto',
			'span-1': 'span 1 / span 1',
			'span-2': 'span 2 / span 2',
			'span-3': 'span 3 / span 3',
			'span-4': 'span 4 / span 4',
			'span-5': 'span 5 / span 5',
			'span-6': 'span 6 / span 6',
		},
		gridRowStart: {
			auto: 'auto',
			'1': '1',
			'2': '2',
			'3': '3',
			'4': '4',
			'5': '5',
			'6': '6',
			'7': '7',
		},
		gridRowEnd: {
			auto: 'auto',
			'1': '1',
			'2': '2',
			'3': '3',
			'4': '4',
			'5': '5',
			'6': '6',
			'7': '7',
		},
		inset: {
			auto: 'auto',
			'-xl': '-10px',
			'-md': '-4px',
			'-sm': '-2px',
			'-3': '-15px',
			0: '0px',
			sm: '2px',
			md: '4px',
			lg: '7px',
			xl: '10px',
			xxl: '40px',
		},
		boxShadow: {
			default: '0 2px 4px 0 rgba(51, 51, 51, .2)',
			lg: '0px 3px 11px 3px rgba(0, 0, 0, 0.2)',
			none: 'none',
		},
		zIndex: {
			'0': 0,
			'1': 1,
			'10': 10,
			'1000': 1000,
		},
		minHeight: (theme, { breakpoints }) => ({
			...theme('spacing'),
			'0': '0px',
			full: '100%',
			1: '120px',
			2: '150px',
			'11-1': '75px',
			'100vh': '100vh',
			...breakpoints(theme('screens')),
		}),
		minWidth: {
			'0': '0px',
			full: '100%',
			115: '115px',
			125: '125px',
		},
		transitionDuration: {
			'75': '75ms',
			'100': '100ms',
			'150': '150ms',
			'200': '200ms',
			'225': '225ms',
			'300': '300ms',
			'500': '500ms',
			'700': '700ms',
			'1000': '1000ms',
		},
	},
	variants: {
		borderWidth: ['responsive', 'first', 'last'],
		borderRadius: ['hover'],
		display: ['responsive', 'hover', 'focus', 'group-hover'],
		padding: ['responsive', 'first', 'last', 'even', 'odd'],
		opacity: ['hover', 'group-hover'],
		margin: ['responsive', 'first', 'last', 'even', 'odd'],
		textColor: ['responsive', 'hover', 'focus', 'group-hover'],
		outline: ['focus'],
		cursor: ['hover', 'responsive'],
		textDecoration: ['hover'],
		minHeight: ['responsive'],
	},
};
